import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { tenantService, licenseService, billingAdminService, userService } from '../services/api';
import { EditTenantModal, ArchiveConfirmModal, AddUserToTenantModal } from '../components/TenantModals';
import { ProductChip, PRODUCT_META, MODULE_LABELS } from '../components/TenantComponents';
import {
  IndianRupee, CreditCard, Gift, Zap, Wallet, AlertTriangle,
  Calendar, Eye,
} from 'lucide-react';
import './BillingPages.css';

const MODULES = ['campaigns', 'devices', 'account'];
const LEVELS = ['admin', 'editor', 'viewer'];
// UI-only sentinel for "no grant row on this module". Never sent over the
// wire: the upsert POST has no way to express deletion, so No-access saves
// route through the per-module DELETE endpoint instead (see handleSubmit).
const NO_ACCESS = 'none';

const TenantDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [licenseSummary, setLicenseSummary] = useState({ total: 0, available: 0, activated: 0, revoked: 0 });
  const [billingAccount, setBillingAccount] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [editPermUser, setEditPermUser] = useState(null);
  // Product enrollment (V276): which product's toggle is in flight, and which
  // product a disable has been requested for (drives the confirm modal).
  const [productLoading, setProductLoading] = useState(null);
  const [disableProduct, setDisableProduct] = useState(null);
  const [invitations, setInvitations] = useState([]);

  const fetchTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tenantService.getById(tenantId, { include: 'stats,users' });
      setTenant(response.data);
      setUsers(response.data?.users || []);
    } catch (err) {
      console.error('Error fetching tenant:', err);
      setError(err.response?.data?.error || 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const fetchLicenses = useCallback(async () => {
    try {
      const response = await licenseService.getAll({ 
        ownerType: 'tenant', 
        ownerId: tenantId,
        pageSize: 100 
      });
      // API returns data array, not licenses array
      const licenseList = response.data || [];
      setLicenses(licenseList);
      
      // Calculate summary
      const summary = {
        total: licenseList.length,
        available: licenseList.filter(l => l.status === 'available').length,
        activated: licenseList.filter(l => l.status === 'activated').length,
        revoked: licenseList.filter(l => l.status === 'revoked').length,
      };
      setLicenseSummary(summary);
    } catch (err) {
      console.error('Error fetching licenses:', err);
    }
  }, [tenantId]);

  const fetchBilling = useCallback(async () => {
    try {
      setBillingLoading(true);
      const data = await billingAdminService.getAccountByTenant(tenantId);
      setBillingAccount(data.account || null);
    } catch (err) {
      console.error('Error fetching billing:', err);
      setBillingAccount(null);
    } finally {
      setBillingLoading(false);
    }
  }, [tenantId]);

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await tenantService.getInvitations(tenantId);
      setInvitations(response.data || []);
    } catch (err) {
      // Invite history is supplementary — a failed fetch must not block the page.
      console.error('Error fetching invitations:', err);
      setInvitations([]);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTenant();
    fetchLicenses();
    fetchBilling();
    fetchInvitations();
  }, [fetchTenant, fetchLicenses, fetchBilling, fetchInvitations]);

  const handleRemoveUser = async (userId) => {
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      await tenantService.removeUser(tenantId, userId);
      setMessage('User removed from tenant successfully');
      fetchTenant();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async () => {
    setActionLoading(true);
    try {
      await tenantService.delete(tenantId);
      setMessage('Tenant archived successfully');
      setShowArchiveConfirm(false);
      fetchTenant();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to archive tenant');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async () => {
    setActionLoading(true);
    try {
      await tenantService.restore(tenantId);
      setMessage('Tenant restored successfully');
      fetchTenant();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore tenant');
    } finally {
      setActionLoading(false);
    }
  };

  // Enroll is idempotent server-side (already-enrolled → 200 with the existing
  // row) so no confirm step; disable goes through DisableProductConfirmModal.
  // Write-then-refetch like every other mutation on this page.
  const handleEnrollProduct = async (p) => {
    setProductLoading(p);
    setMessage(null);
    setError(null);
    try {
      await tenantService.enrollProduct(tenantId, p);
      setMessage(`${PRODUCT_META[p].label} enabled`);
      fetchTenant();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enable product');
    } finally {
      setProductLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return { class: 'status-active', label: 'Active', desc: 'Tenant is operational' };
    }
    return { class: 'status-disabled', label: 'Archived', desc: 'Tenant is archived' };
  };

  const getLicenseStatusBadge = (status) => {
    const badges = {
      'unowned': { class: 'status-pending', label: 'Unowned' },
      'available': { class: 'status-pending', label: 'Available' },
      'activated': { class: 'status-active', label: 'Activated' },
      'revoked': { class: 'status-disabled', label: 'Revoked' },
    };
    return badges[status] || { class: '', label: status };
  };

  if (loading) {
    return (
      <div className="user-detail-page">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  if (error && !tenant) {
    return (
      <div className="user-detail-page">
        <div className="page-header">
          <Link to="/tenants" className="back-link">← Back to Tenants</Link>
        </div>
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  const statusBadge = getStatusBadge(tenant?.isActive);

  // Which permission modules the members editor may show/write, derived from
  // product enrollment, fail-closed: campaigns iff brands_manager, devices iff
  // media_console, account always (workspace administration is not a product).
  // The pre-V276-server fallback (absent products => all modules) is gone: the
  // server has sent products (|| []) on list and detail since V276 and deploys
  // with this client, so a missing key can only be a response-shape regression
  // — which must narrow the editor to account-only, not open every module.
  const enrolledProducts = new Set((tenant?.products || []).map(p => p.product));
  const enrolledModules = MODULES.filter(m => m === 'account'
    || (m === 'campaigns' && enrolledProducts.has('brands_manager'))
    || (m === 'devices' && enrolledProducts.has('media_console')));

  return (
    <div className="user-detail-page">
      <div className="page-header">
        <Link to="/tenants" className="back-link">← Back to Tenants</Link>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
            Edit Tenant
          </button>
          <div className="actions-dropdown">
            <button className="btn btn-secondary">
              {actionLoading ? '...' : 'Actions ▼'}
            </button>
            <div className="dropdown-menu">
              {tenant?.isActive ? (
                <button className="dropdown-item text-danger" onClick={() => setShowArchiveConfirm(true)}>
                  Archive Tenant
                </button>
              ) : (
                <button className="dropdown-item text-success" onClick={handleRestore}>
                  Restore Tenant
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Tenant Profile Card */}
      <div className="user-profile-card">
        <div className="profile-header">
          <div className="user-avatar large" aria-label={`Avatar for ${tenant?.name}`}>
            {tenant?.name?.charAt(0) || '?'}
          </div>
          <div className="profile-info">
            <h1>{tenant?.name || 'Unknown Tenant'}</h1>
            <p className="email">{tenant?.description || 'No description'}</p>
            <span className={`status-badge ${statusBadge.class}`}>
              {statusBadge.label}
            </span>
            <span className="status-desc">{statusBadge.desc}</span>
            {tenant?.isPersonal && (
              <span style={{ marginLeft: '0.5rem', padding: '2px 8px', borderRadius: '4px', background: '#e3f2fd', color: '#1565c0', fontSize: '0.85rem' }}>
                Personal Workspace
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Account Info Section */}
      <div className="detail-section">
        <h2>Tenant Information</h2>
        <div className="detail-grid">
          <div className="detail-item">
            <label>Tenant ID</label>
            <span className="mono">{tenant?.id || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Name</label>
            <span>{tenant?.name || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Status</label>
            <span>{tenant?.isActive ? 'Active' : 'Archived'}</span>
          </div>
          <div className="detail-item">
            <label>Description</label>
            <span>{tenant?.description || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <label>Type</label>
            <span>
              {tenant?.isPersonal ? (
                <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#e3f2fd', color: '#1565c0', fontSize: '0.85rem' }}>Personal Workspace</span>
              ) : (
                <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#f3e5f5', color: '#7b1fa2', fontSize: '0.85rem' }}>Organization</span>
              )}
            </span>
          </div>
          {tenant?.ownerUserId && (
            <div className="detail-item">
              <label>Owner</label>
              <span>
                <Link to={`/users/${encodeURIComponent(tenant.ownerEmail || tenant.ownerUserId)}`}>
                  {tenant.ownerName || tenant.ownerEmail || tenant.ownerUserId}
                </Link>
              </span>
            </div>
          )}
          <div className="detail-item">
            <label>Users</label>
            <span>
              <Link to={`/users?tenantId=${tenantId}`} title="View users in this tenant">
                {tenant?.stats?.userCount || 0}
              </Link>
            </span>
          </div>
          <div className="detail-item">
            <label>Campaigns</label>
            <span>
              <Link to={`/campaigns?tenant=${tenantId}`} title="View this tenant's campaigns">
                {tenant?.stats?.campaignCount || 0}
              </Link>
            </span>
          </div>
          <div className="detail-item">
            <label>Devices</label>
            <span>
              <Link to={`/devices?tenantId=${tenantId}`} title="View devices assigned to this tenant">
                {tenant?.stats?.deviceCount || 0}
              </Link>
            </span>
          </div>
          <div className="detail-item">
            <label>Created</label>
            <span>{formatDate(tenant?.createdAt)}</span>
          </div>
          <div className="detail-item">
            <label>Last Updated</label>
            <span>{formatDate(tenant?.updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Product Enrollment Section (V276) — sits ABOVE the members editor
          because enrollment gates which module columns that editor shows. */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Product Enrollment</h2>
        </div>
        <div className="tenant-list">
          {['brands_manager', 'media_console', 'gridrock'].map((p) => {
            const row = (tenant?.products || []).find(x => x.product === p);
            return (
              <div key={p} className="tenant-assignment-item">
                <div className="tenant-info">
                  <ProductChip product={p} />
                  <span className="tenant-name">{PRODUCT_META[p].label}</span>
                </div>
                <div className="tenant-role">
                  {row ? (
                    <span className="tenant-plan">
                      Enabled {formatDate(row.enabled_at)}
                      {(row.enabled_by_name || row.enabled_by)
                        ? ` by ${row.enabled_by_name || row.enabled_by}`
                        : row.source ? ` (${row.source})` : ''}
                    </span>
                  ) : (
                    <span className="tenant-plan" style={{ color: '#999' }}>Not enrolled</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  {row ? (
                    <button
                      className="btn btn-danger btn-sm"
                      disabled={productLoading === p}
                      onClick={() => setDisableProduct(p)}
                    >
                      {productLoading === p ? '...' : 'Disable'}
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={!tenant?.isActive || productLoading === p}
                      onClick={() => handleEnrollProduct(p)}
                      title={!tenant?.isActive ? 'Archived tenants cannot enroll products' : undefined}
                    >
                      {productLoading === p ? '...' : 'Enable'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* User Assignments Section */}
      <div className="detail-section">
        <div className="section-header">
          <h2>User Assignments</h2>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={() => setShowAddUserModal(true)}
            disabled={!tenant?.isActive}
          >
            + Add User
          </button>
        </div>
        {users.length === 0 ? (
          <div className="empty-state-small">
            <p>No users assigned to this tenant yet.</p>
            <p className="muted">Click "Add User" to assign users to this tenant.</p>
          </div>
        ) : (
          <div className="tenant-list">
            {users.map((user) => (
              <div key={user.userId} className="tenant-assignment-item">
                <div className="tenant-info">
                  <Link to={`/users/${encodeURIComponent(user.email || user.userId)}`} className="tenant-name">
                    {user.name || user.email}
                  </Link>
                  <span className="tenant-plan">({user.email})</span>
                </div>
                <div className="tenant-role" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {user.permissions ? (
                    // Only enrolled modules are shown: a grant on a module whose
                    // product is not enrolled still exists in the DB, but it is
                    // dormant and displaying it here would misstate reality.
                    Object.entries(user.permissions)
                      .filter(([mod]) => enrolledModules.includes(mod))
                      .map(([mod, level]) => {
                        const colors = { admin: '#dc3545', editor: '#fd7e14', viewer: '#6c757d' };
                        return (
                          <span key={mod} style={{
                            display: 'inline-block', padding: '2px 6px', borderRadius: '4px',
                            fontSize: '0.75rem', background: `${colors[level] || '#6c757d'}15`,
                            color: colors[level] || '#6c757d', border: `1px solid ${colors[level] || '#6c757d'}30`,
                          }}>
                            {MODULE_LABELS[mod] || mod}: {level}
                          </span>
                        );
                      })
                  ) : (
                    <span className="role-badge">no permissions</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEditPermUser(user)}
                    disabled={actionLoading}
                    title="Edit permissions"
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemoveUser(user.userId)}
                    disabled={actionLoading}
                    title="Remove from tenant"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invitations Section — invite HISTORY, read-only. Invites are sent from
          the product consoles by tenant admins; this admin view only witnesses
          them (who vouched for whom, and whether the door was used). */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Invitations</h2>
        </div>
        {invitations.length === 0 ? (
          <div className="empty-state-small">
            <p>No invitations sent from this tenant yet.</p>
          </div>
        ) : (
          <div className="data-table" style={{ fontSize: '0.85rem' }}>
            <table>
              <thead>
                <tr>
                  <th>Invitee</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Invited By</th>
                  <th>Sent</th>
                  <th>Accepted / Expires</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => {
                  const invStatusBadge = {
                    pending: 'status-pending',
                    accepted: 'status-active',
                    revoked: 'status-disabled',
                    expired: 'status-disabled',
                    // Legal since V209 widened the CHECK; terminal like its siblings.
                    declined: 'status-disabled',
                  }[inv.status] || '';
                  return (
                    <tr key={inv.id}>
                      <td>{inv.email}</td>
                      <td style={{ textTransform: 'capitalize' }}>{inv.role}</td>
                      <td>
                        <span className={`status-badge ${invStatusBadge}`} style={{ textTransform: 'capitalize' }}>
                          {inv.status}
                        </span>
                      </td>
                      {/* login-service returns the inviter as a nested object
                          invitedBy: {id, name, email} | null (null when the
                          inviter user was deleted — ON DELETE SET NULL, V208);
                          the proxy passes it through verbatim. */}
                      <td>{inv.invitedBy?.name || inv.invitedBy?.email || <span style={{ color: '#999' }}>—</span>}</td>
                      <td>{formatDate(inv.createdAt || inv.created_at)}</td>
                      <td>
                        {inv.status === 'accepted'
                          ? formatDate(inv.acceptedAt || inv.accepted_at)
                          : formatDate(inv.expiresAt || inv.expires_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Billing Summary Card */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Billing</h2>
          {billingAccount && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/billing/accounts')}>
              View Full Account
            </button>
          )}
        </div>
        {billingLoading ? (
          <div style={{ padding: '1rem', color: '#999', fontSize: '0.9rem' }}>Loading billing data…</div>
        ) : !billingAccount ? (
          <div className="no-billing-account">
            <CreditCard size={28} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
            <p>No billing account</p>
            <p style={{ fontSize: '0.8rem', color: '#bbb' }}>A billing account will be auto-created when this tenant first accesses billing APIs</p>
          </div>
        ) : (
          <div className="tenant-billing-card">
            <h3>
              <IndianRupee size={16} /> Billing Account
              <span className={`status-badge ${billingAccount.status === 'active' ? 'status-active' : billingAccount.status === 'suspended' ? 'status-pending' : 'status-disabled'}`} style={{ marginLeft: 8 }}>
                {billingAccount.status}
              </span>
            </h3>

            {/* Alerts */}
            {billingAccount.spending_limit_reached && (
              <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#dc3545', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={14} /> Spending limit reached
              </div>
            )}
            {parseInt(billingAccount.payment_failure_count) > 0 && (
              <div style={{ background: '#fff9e6', border: '1px solid #ffc107', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#856404', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={14} /> {billingAccount.payment_failure_count} payment failure(s)
              </div>
            )}

            <div className="tenant-billing-grid">
              <div className="tenant-billing-item">
                <span className="tbi-label">Mode</span>
                <span className="tbi-value">
                  {billingAccount.billing_mode === 'threshold' && <Zap size={13} />}
                  {billingAccount.billing_mode === 'prepaid' && <Wallet size={13} />}
                  {billingAccount.billing_mode === 'monthly' && <Calendar size={13} />}
                  {billingAccount.billing_mode}
                </span>
              </div>
              <div className="tenant-billing-item">
                <span className="tbi-label">Threshold</span>
                <span className="tbi-value">
                  <span style={{ padding: '1px 6px', borderRadius: 4, background: '#e8e8ff', color: '#4f46e5', fontSize: '0.8rem', fontWeight: 600 }}>
                    L{billingAccount.billing_threshold_level}
                  </span>
                  ₹{Number(billingAccount.billing_threshold_amount || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="tenant-billing-item">
                <span className="tbi-label">Accrued Spend</span>
                <span className="tbi-value" style={{ color: parseFloat(billingAccount.accrued_spend) > parseFloat(billingAccount.billing_threshold_amount) ? '#dc3545' : '#333' }}>
                  ₹{Number(billingAccount.accrued_spend || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="tenant-billing-item">
                <span className="tbi-label">Credit Balance</span>
                <span className="tbi-value" style={{ color: parseFloat(billingAccount.credit_balance) > 0 ? '#27ae60' : '#999' }}>
                  <Gift size={13} style={{ color: '#27ae60' }} />
                  ₹{Number(billingAccount.credit_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
              {parseFloat(billingAccount.prepaid_balance) > 0 && (
                <div className="tenant-billing-item">
                  <span className="tbi-label">Prepaid Balance</span>
                  <span className="tbi-value">
                    <Wallet size={13} />
                    ₹{Number(billingAccount.prepaid_balance).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <div className="tenant-billing-item">
                <span className="tbi-label">Payment Methods</span>
                <span className="tbi-value">
                  {parseInt(billingAccount.active_payment_methods) > 0 ? (
                    <><CreditCard size={13} style={{ color: '#27ae60' }} /> {billingAccount.active_payment_methods} active</>
                  ) : (
                    <span style={{ color: '#dc3545' }}><AlertTriangle size={13} /> None</span>
                  )}
                </span>
              </div>
              <div className="tenant-billing-item">
                <span className="tbi-label">Charges</span>
                <span className="tbi-value">
                  {billingAccount.successful_charge_count || 0} successful
                  {parseInt(billingAccount.failed_charges_7d) > 0 && (
                    <span style={{ color: '#dc3545', marginLeft: 4 }}>({billingAccount.failed_charges_7d} failed 7d)</span>
                  )}
                </span>
              </div>
              <div className="tenant-billing-item">
                <span className="tbi-label">Last Charge</span>
                <span className="tbi-value" style={{ fontSize: '0.85rem', color: '#888' }}>
                  {billingAccount.last_charge_at ? new Date(billingAccount.last_charge_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Never'}
                </span>
              </div>
            </div>

            <div className="tenant-billing-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/billing/accounts')}>
                <Eye size={14} /> View Billing Account
              </button>
              <Link to="/billing/credits" className="btn btn-secondary btn-sm">
                <Gift size={14} /> Credits
              </Link>
              <Link to="/billing/transactions" className="btn btn-secondary btn-sm">
                <IndianRupee size={14} /> Transactions
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Licenses Section */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Licenses</h2>
          <Link to="/licenses" className="btn btn-secondary btn-sm">
            View All Licenses
          </Link>
        </div>
        
        {/* License Summary */}
        <div className="license-summary" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div className="summary-card" style={{ padding: '0.75rem 1rem', background: '#f5f5f5', borderRadius: '8px', minWidth: '100px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{licenseSummary.total}</div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Total</div>
          </div>
          <div className="summary-card" style={{ padding: '0.75rem 1rem', background: '#fff9e6', borderRadius: '8px', minWidth: '100px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#b8860b' }}>{licenseSummary.available}</div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Available</div>
          </div>
          <div className="summary-card" style={{ padding: '0.75rem 1rem', background: '#e6f7e6', borderRadius: '8px', minWidth: '100px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#228b22' }}>{licenseSummary.activated}</div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Activated</div>
          </div>
          <div className="summary-card" style={{ padding: '0.75rem 1rem', background: '#ffe6e6', borderRadius: '8px', minWidth: '100px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#dc3545' }}>{licenseSummary.revoked}</div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Revoked</div>
          </div>
        </div>

        {/* License List */}
        {licenses.length === 0 ? (
          <div className="empty-state-small">
            <p>No licenses assigned to this tenant yet.</p>
            <p className="muted">Generate new licenses and assign them to this tenant.</p>
          </div>
        ) : (
          <div className="tenant-list">
            {licenses.slice(0, 10).map((license) => (
              <div key={license.id} className="tenant-assignment-item">
                <div className="tenant-info">
                  <Link to={`/licenses/${license.id}`} className="tenant-name" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {license.licenseKey}
                  </Link>
                  {license.device && (
                    <Link to={`/ggos-devices/${license.device.thingName}`} className="tenant-plan" style={{ marginLeft: '0.5rem' }}>
                      {license.device.thingName}
                    </Link>
                  )}
                </div>
                <div className="tenant-role">
                  <span className={`status-badge ${getLicenseStatusBadge(license.status).class}`}>
                    {getLicenseStatusBadge(license.status).label}
                  </span>
                </div>
                <Link to={`/licenses/${license.id}`} className="btn btn-secondary btn-sm">
                  View
                </Link>
              </div>
            ))}
            {licenses.length > 10 && (
              <div style={{ textAlign: 'center', padding: '0.5rem', color: '#666' }}>
                ... and {licenses.length - 10} more licenses. <Link to={`/licenses?ownerType=tenant&ownerId=${tenantId}`}>View all</Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Tenant Modal */}
      {showEditModal && (
        <EditTenantModal
          tenant={tenant}
          onClose={() => setShowEditModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowEditModal(false); fetchTenant(); }}
          onError={setError}
        />
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserToTenantModal
          tenantId={tenantId}
          enrolledModules={enrolledModules}
          onClose={() => setShowAddUserModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowAddUserModal(false); fetchTenant(); }}
          onError={setError}
        />
      )}

      {/* Edit Permissions Inline Modal */}
      {editPermUser && (
        <EditPermissionsInlineModal
          user={editPermUser}
          tenantId={tenantId}
          tenantName={tenant?.name}
          enrolledModules={enrolledModules}
          onClose={() => setEditPermUser(null)}
          onSuccess={(msg) => { setMessage(msg); setEditPermUser(null); fetchTenant(); }}
          onError={setError}
        />
      )}

      {/* Disable Product Confirmation Modal */}
      {disableProduct && (
        <DisableProductConfirmModal
          tenantId={tenantId}
          tenantName={tenant?.name}
          product={disableProduct}
          onClose={() => setDisableProduct(null)}
          onDisabled={(msg) => { setMessage(msg); setDisableProduct(null); fetchTenant(); }}
        />
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <ArchiveConfirmModal
          tenant={tenant}
          onClose={() => setShowArchiveConfirm(false)}
          onConfirm={handleArchive}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

// Inline Edit Permissions Modal for TenantDetail.
// `enrolledModules` gates both what is SHOWN and what is WRITTEN: the state
// still seeds all three modules with viewer defaults, so without the submit
// filter below, editing any member of a partially-enrolled tenant would
// silently write viewer rows for the hidden modules — re-granting exactly the
// access the enrollment gate removed. The payload filter is correctness, not
// cosmetics. (POST /users/:id/tenants upserts only the modules posted, so
// omitting a module leaves its existing row untouched.)
const EditPermissionsInlineModal = ({ user, tenantId, tenantName, enrolledModules, onClose, onSuccess, onError }) => {
  const mods = enrolledModules || MODULES;
  // Seed absent modules as No-access, not viewer: the editor must show what is
  // STORED, and (now that the diff below routes leveled modules through the
  // upsert) a viewer seed on an absent module would silently grant it on an
  // untouched save.
  const [permissions, setPermissions] = useState({
    campaigns: user.permissions?.campaigns || NO_ACCESS,
    devices: user.permissions?.devices || NO_ACCESS,
    account: user.permissions?.account || NO_ACCESS,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const setAllPermissions = (level) => {
    setPermissions(prev => {
      const next = { ...prev };
      mods.forEach(m => { next[m] = level; });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Diff against the member's CURRENT permissions. The upsert endpoint
      // writes only the modules posted and cannot delete a row — the
      // per-module DELETE is the revoke path — so the save splits three ways:
      //   No-access + row EXISTS   → DELETE that module row (sequentially),
      //   No-access + row absent   → nothing to do on either endpoint,
      //   leveled                  → existing upsert POST, as before.
      const desired = Object.entries(permissions).filter(([m]) => mods.includes(m));
      const toRevoke = desired
        .filter(([m, level]) => level === NO_ACCESS && user.permissions?.[m])
        .map(([m]) => m);
      const payload = Object.fromEntries(desired.filter(([, level]) => level !== NO_ACCESS));
      for (const mod of toRevoke) {
        try {
          await userService.removeModulePermission(user.userId, tenantId, mod);
        } catch (err) {
          // 404 = the row is already gone (concurrent revoke) — the end state
          // this save wanted; anything else aborts the save.
          if (err.response?.status !== 404) throw err;
        }
      }
      if (Object.keys(payload).length > 0) {
        await tenantService.addUser(tenantId, user.userId, payload);
      }
      onSuccess?.('Permissions updated successfully');
    } catch (err) {
      onError?.(err.response?.data?.error || 'Failed to update permissions');
    } finally {
      setSubmitting(false);
    }
  };

  const permColors = { admin: '#dc3545', editor: '#fd7e14', viewer: '#6c757d' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h2>Edit Permissions</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>{user.name || user.email}</strong> in <strong>{tenantName}</strong>
            </p>
            <div style={{ background: '#f8f9fa', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>
              <span style={{ color: '#666' }}>Current: </span>
              {user.permissions && Object.entries(user.permissions)
                .filter(([mod]) => mods.includes(mod))
                .map(([mod, level]) => (
                  <span key={mod} style={{
                    display: 'inline-block', padding: '1px 5px', borderRadius: '3px', marginRight: '4px',
                    background: `${permColors[level]}15`, color: permColors[level], fontSize: '0.75rem',
                    border: `1px solid ${permColors[level]}30`,
                  }}>
                    {MODULE_LABELS[mod] || mod}: {level}
                  </span>
                ))}
            </div>
            <div className="form-group">
              <label>New Permissions</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.85rem', color: '#666' }}>Quick set:</span>
                {LEVELS.map(level => (
                  <button key={level} type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setAllPermissions(level)} style={{ textTransform: 'capitalize' }}>
                    All {level}
                  </button>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Module</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Permission</th>
                  </tr>
                </thead>
                <tbody>
                  {mods.map(mod => (
                    <tr key={mod} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '0.5rem', fontWeight: 500 }}>
                        {MODULE_LABELS[mod] || mod}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <select value={permissions[mod]}
                          onChange={e => setPermissions(prev => ({ ...prev, [mod]: e.target.value }))}
                          style={{ width: '100%' }}>
                          {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                          <option value={NO_ACCESS}>No access</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Update Permissions'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Confirm-on-disable for product enrollment (patterned on ArchiveConfirmModal:
// modal-overlay → modal modal-sm → warning-list). Owns the unenroll call and
// its own error state so a DEVICES_STILL_ASSIGNED 409 renders INSIDE the modal
// without closing it — the operator reads why, follows the deep link, retries.
const DisableProductConfirmModal = ({ tenantId, tenantName, product, onClose, onDisabled }) => {
  const [loading, setLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleConfirm = async () => {
    setLoading(true);
    setModalError(null);
    try {
      await tenantService.unenrollProduct(tenantId, product);
      onDisabled(`${PRODUCT_META[product].label} disabled`);
    } catch (err) {
      // 409 bodies are top-level { error, code, count } — read `code`, never
      // render the object itself.
      if (err.response?.status === 409 && err.response?.data?.code === 'DEVICES_STILL_ASSIGNED') {
        const count = err.response.data.count;
        setModalError(
          <>
            Media Console can't be disabled while {count != null ? count : 'some'} device{count === 1 ? ' is' : 's are'} still
            assigned to this tenant. Return them to inventory first.{' '}
            <Link to={`/devices?tenantId=${tenantId}`}>View assigned devices →</Link>
          </>
        );
      } else {
        setModalError(err.response?.data?.error || 'Failed to disable product');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Disable {PRODUCT_META[product].label}?</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {modalError && <div className="alert alert-error">{modalError}</div>}
          <p>Disable <strong>{PRODUCT_META[product].label}</strong> for <strong>{tenantName}</strong>?</p>
          <ul className="warning-list">
            <li>Members lose access to this product's console</li>
            <li>Enrollment history is kept — you can re-enable later</li>
          </ul>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Disabling...' : `Disable ${PRODUCT_META[product].label}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantDetail;
